import './App.css';
import './main.css';


import * as React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Navigator from './Components/Navigator';
import Home from './Components/Home';
import Header from './Components/Header';

import * as Sentry from '@sentry/react';
import {
  Route,
  Routes,
  matchPath,
  useLocation,
} from 'react-router-dom';
import Users from './Components/Users';
import Customers from "./Components/Customers";
import RentalConfigs from './Components/Rental/RentalConfigs';
import Login from './Components/Login';
import { isLoggedIn, foundUser, send_authorized_request, logout } from './Constants/cognito'
import { CircularProgress } from '@mui/material';
import { all_locations_object, API_URL, AXIOS_API_INSTANCE, BASE_AXIOS_API_INSTANCE, SET_CLIENT_ID, SERVICE_ORDER_STATUS_TYPES } from './Constants';
import { useTrackedDarkMode, useTrackedLocation, useTrackedUser, useTrackedTheme, useTrackedUserOpenServiceOrderWorkHistory } from './Components/UserState'
import Locations from './Components/Locations';
import Reserve from './Components/Rental/Reserve';
import { useCookies } from 'react-cookie';
import { useSnackbar } from 'notistack';
import RentalItems from './Components/Rental/RentalItems';
import Contracts from './Components/Rental/Contracts';
import Reports from './Components/Reports';
import ServiceOrders from './Components/Service/ServiceOrders';
import NewServiceOrder from './Components/Service/NewServiceOrder';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import ServiceKits from './Components/Service/ServiceKits';
import ClientConfiguration from './Components/ClientConfiguration';
import ServiceOrderItemTypes from './Components/Service/ServiceOrderItemTypes';
import InventoryItems from './Components/Inventory/InventoryItems';
import ServiceOrderWarrantiesList from './Components/Service/ServiceOrderWarrantiesList';
import { getTheme } from './theme';
import ServiceOrderParts from './Components/Service/ServiceOrderParts';
import ServiceOrderFees from './Components/Service/ServiceOrderFees';
import SMS from './Components/CRM/sms';
import ServiceTechnicianWorkHistory from './Components/Service/ServiceTechnicianWorkHistory';
import InfoModal from './Components/modals/InfoModal';
import Dashboard from './Components/Dashboard';



const drawerWidth = 256;

function useRouteMatch(patterns) {
  const { pathname } = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

const setup_reroute_handler = (setLogin, set_my_user) => {
  window.addEventListener('custom_logout', e => {
    setLogin(false)
    set_my_user({})
  }, true)
}

const get_my_user = (set_my_user, setSelectedLocation, setOpenServiceOrderWorkHistory, cookies, setCookie, enqueueSnackbar, theme, setTheme) => {
  send_authorized_request(() => BASE_AXIOS_API_INSTANCE.get("my_user").then((user) => {
    AXIOS_API_INSTANCE.defaults.baseURL = `${API_URL}/${user.data.client.id}`
    SET_CLIENT_ID(user.data.client.id)
    setCookie('CloudFront-Key-Pair-Id', user.data.cf_cookies['CloudFront-Key-Pair-Id'], { path: '/' })
    setCookie('CloudFront-Policy', user.data.cf_cookies['CloudFront-Policy'], { path: '/' })
    setCookie('CloudFront-Signature', user.data.cf_cookies['CloudFront-Signature'], { path: '/' })
    var sorted_service_order_statuses = {}
    Object.keys(user.data.client.service_order_statuses).sort(
      (a, b) => {
        const real_a = user.data.client.service_order_statuses[a]
        const real_b = user.data.client.service_order_statuses[b]
        if (real_a.type !== real_b.type) {
          return SERVICE_ORDER_STATUS_TYPES.indexOf(real_a.type) - SERVICE_ORDER_STATUS_TYPES.indexOf(real_b.type)
        } else {
          return a.localeCompare(b)
        }
      }).forEach((status) => {
        sorted_service_order_statuses[status] = user.data.client.service_order_statuses[status]
      })
    user.data.client.service_order_statuses = sorted_service_order_statuses
    let palettes = {}
    Object.keys(user.data.client.service_order_statuses).forEach((status) => {
      palettes[user.data.client.service_order_statuses[status].custom_color] = theme.palette.augmentColor({
        color: {
          'main': user.data.client.service_order_statuses[status].custom_color,
        },
        name: user.data.client.service_order_statuses[status].custom_color
      })
    })
    Object.keys(user.data.client.service_order_parts_statuses).forEach((status) => {
      palettes[user.data.client.service_order_parts_statuses[status].custom_color] = theme.palette.augmentColor({
        color: {
          'main': user.data.client.service_order_parts_statuses[status].custom_color,
        },
        name: user.data.client.service_order_parts_statuses[status].custom_color
      })
    })
    if (user.data.client.client_color) {
      palettes['brandcolor'] = theme.palette.augmentColor({
        color: {
          'main': user.data.client.client_color,
        },
        name: 'brandcolor'
      })
    }
    const my_theme = createTheme(theme, {
      palette: palettes
    })
    setTheme(my_theme)
    set_my_user({ ...user.data.data, client: user.data.client, notice: user.data?.notice });
    Sentry.setUser({
      name: user.data.data.name,
      email: user.data.data.email,
    })
    setOpenServiceOrderWorkHistory(user.data.open_service_order_work_history);
    const current_location = user.data.data.locations.find((loc) => loc.id.toString() === localStorage.getItem(`selected_location`))
    if (current_location) {
      setSelectedLocation(current_location)
    } else if (user.data.data.locations.length === 1) {
      setSelectedLocation(user.data.data.locations[0])
    } else {
      setSelectedLocation(all_locations_object)
    }
  }).catch((error) => {
    console.log(error);
    logout()
    enqueueSnackbar(`${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
  }));
}

export default function App() {
  const [initBodyOverlayScrollbars,] =
    useOverlayScrollbars({
      defer: true,
    });
  React.useEffect(() => {
    initBodyOverlayScrollbars(document.body);
  }, [initBodyOverlayScrollbars]);

  const [darkMode,] = useTrackedDarkMode();
  const [theme, setTheme] = useTrackedTheme();
  const [cookies, setCookie] = useCookies()
  const { enqueueSnackbar } = useSnackbar();
  const [login, setLogin] = React.useState(false)
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [my_user, set_my_user] = useTrackedUser();
  const [showSystemNotice, setShowSystemNotice] = React.useState(true);
  const [mfaStatus, setMfaStatus] = React.useState(undefined);
  const [selectedLocation, setSelectedLocation] = useTrackedLocation();
  const [, setOpenServiceOrderWorkHistory] = useTrackedUserOpenServiceOrderWorkHistory();
  const [openChat, setOpenChat] = React.useState(false);
  const isMdUp = useMediaQuery((theme ?? getTheme(darkMode)).breakpoints.up('md'));
  console.log('RENDERING APP')
  const my_theme = theme

  React.useEffect(() => {
    setTheme({
      ...createTheme(theme ?? getTheme(darkMode), {
        palette: {
          mode: darkMode ? 'dark' : 'light'
        }
      })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const routeMatch = useRouteMatch(['/dashboard', '/login', '/users', '/customers', '/locations', '/reports', '/client',
    '/rentalitems', '/rentalconfigs', '/reserve', '/contracts', '/contracts/:id',
    '/newserviceorder', '/serviceorders', '/serviceorders/:id', '/serviceorderparts', '/servicekits', '/serviceorderitemtypes', '/serviceorderfees', '/servicetechnicianhistory',
    '/serviceorderwarrantyclaims',
    '/inventoryitems']);
  const currenttab = routeMatch?.pattern?.path || "";
  const handle_login = () => {
    isLoggedIn(setLogin, login, set_my_user, get_my_user, setSelectedLocation, setOpenServiceOrderWorkHistory, cookies, setCookie, enqueueSnackbar, mfaStatus, setMfaStatus, theme, setTheme)
  }

  React.useEffect(() => {
    setTheme(getTheme(darkMode))
    setup_reroute_handler(setLogin, set_my_user)
    handle_login()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if ((!foundUser() && !login)) {
    return (
      <ThemeProvider theme={getTheme(false)}>
        <Login login_callback={handle_login}></Login>
      </ThemeProvider>
    )
  }
  if ((foundUser() && !login) || my_user?.id === undefined) {
    let circle_size = Math.round(((window.innerWidth + window.innerHeight) / 2) * .10).toString()
    const circle_px = circle_size + 'px'
    return (
      <ThemeProvider theme={getTheme(false)}>
        <Box
          sx={{
            margin: '10% auto',
            display: 'grid',
            justifyContent: 'center',
            rowGap: '20px'
          }}
        >
          <Box
            component="img"
            sx={{
              display: 'block',
              height: (circle_size * 2) + 'px'
            }}
            src="/cedartitanslogo.png"
          />
          <CircularProgress style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', height: circle_px, width: circle_px }} color="primary" />
        </Box>
      </ThemeProvider>)
  }

  return (
    <ThemeProvider theme={my_theme ?? getTheme(false)}>
      <CssBaseline />

      <Box sx={{ display: 'flex', minHeight: '100dvh', height: 'auto', maxWidth: '100vw' }}>
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          {isMdUp ?
            <Navigator
              PaperProps={{ style: { width: drawerWidth } }}
              sx={{ display: { md: 'block', sm: 'none', xs: 'none' } }}
              currenttab={currenttab}
              my_user={my_user}
              selectedLocation={selectedLocation}
              setOpenChat={setOpenChat}
            />
            :
            <Navigator
              PaperProps={{ style: { width: drawerWidth, height: '100%' } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              currenttab={currenttab}
              my_user={my_user}
              selectedLocation={selectedLocation}
              setOpenChat={setOpenChat}
            />
          }


        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100dvh', maxWidth: '100vw' }}>
          <Header onDrawerToggle={handleDrawerToggle} currenttab={currenttab} mfaStatus={mfaStatus} setMfaStatus={setMfaStatus} />
          <Box component="main" sx={{ flex: 1, py: '12px', px: '12px', display: 'flex' }}>
            <Routes>
              <Route path="*" element={<Home />} />
              <Route path="/Dashboard" element={<Dashboard />} />
              <Route path="/Users" element={<Users />} />
              <Route path="/Locations" element={<Locations />} />
              <Route path="/Customers" element={<Customers />} />
              <Route path="/Reports" element={<Reports />} />
              <Route path="/Client" element={<ClientConfiguration />} />
              {/* Rental */}
              <Route path="/RentalConfigs" element={<RentalConfigs />} />
              <Route path="/RentalItems" element={<RentalItems />} />
              <Route path="/Reserve" element={<Reserve />} />
              <Route path="/Contracts" element={<Contracts />}>
                <Route path={':contractId'} />
                <Route path={''} />
              </Route>
              {/* Service */}
              <Route path="/ServiceTechnicianHistory" element={<ServiceTechnicianWorkHistory />} />
              <Route path="/NewServiceOrder" element={<NewServiceOrder />} />
              <Route path="/ServiceOrders" element={<ServiceOrders />}>
                <Route path={':serviceOrderId'} />
                <Route path={''} />
              </Route>
              <Route path="/ServiceOrderParts" element={<ServiceOrderParts />} />
              <Route path="/ServiceKits" element={<ServiceKits />} />
              <Route path="/ServiceOrderItemTypes" element={<ServiceOrderItemTypes />} />
              <Route path="/ServiceOrderFees" element={<ServiceOrderFees />} />
              <Route path="/ServiceOrderWarrantyClaims" element={<ServiceOrderWarrantiesList />} />
              {/* Inventory */}
              <Route path="/InventoryItems" element={<InventoryItems />} />
            </Routes>
            <InfoModal
              open={showSystemNotice && Boolean(my_user?.notice)}
              callback={() => { setShowSystemNotice(false) }}
              title={my_user?.notice?.title}
              content={my_user?.notice?.text}
              maxWidth='md'
              markdown={true}
            />
            <SMS
              open={openChat}
              onClose={() => { setOpenChat(false) }}
            />
          </Box>

        </Box>

      </Box>
    </ThemeProvider>
  );
}